import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Search, PenLine, Barcode, Check, AlertCircle, X, Plus, Loader2 } from 'lucide-react';
import { FREQUENCY_OPTIONS, createMedication, searchDrugs, lookupNDC } from '@/lib/medications';
import { checkOnAdd, stashPostAddToast, stashPendingSevere } from '@/lib/interactions';
import { SEVERITY_ORDER } from '@/lib/theme';
import { defaultTimesForFrequency } from '@/lib/doses';

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const MODES = [
  { key: 'search', label: 'Search', icon: Search },
  { key: 'manual', label: 'Manual', icon: PenLine },
  { key: 'barcode', label: 'NDC', icon: Barcode },
];

export default function AddMedicationPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('search');
  const [name, setName] = useState('');
  const [rxcui, setRxcui] = useState(null);
  const [unverifiedHint, setUnverifiedHint] = useState(false);
  const [barcodeNdc, setBarcodeNdc] = useState(null);
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('once daily');
  const [scheduleTimes, setScheduleTimes] = useState([]);
  const [pendingTime, setPendingTime] = useState('');
  const [notes, setNotes] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ndcInput, setNdcInput] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (mode !== 'search') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try { setResults(await searchDrugs(q, 8)); } catch {} finally { setSearching(false); }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, mode]);

  const pickSearchHit = (hit) => { setName(hit.name); setRxcui(hit.rxcui ?? null); setUnverifiedHint(false); setQuery(hit.name); setResults([]); };

  const onNdcLookup = async () => {
    if (!ndcInput.trim()) return;
    setError(null);
    setBarcodeNdc(ndcInput.trim());
    try {
      const found = await lookupNDC(ndcInput.trim());
      if (found) {
        setName(found.brand_name || found.name || found.generic_name || '');
        setRxcui(found.rxcui ?? null);
        setUnverifiedHint(!found.rxcui);
        setMode('search');
        setQuery(found.brand_name || found.name || '');
      } else {
        setError(`NDC ${ndcInput.trim()} not recognised. Switch to manual entry.`);
        setMode('manual');
      }
    } catch (e) { setError(e.message); }
  };

  const addTime = () => {
    const t = pendingTime.trim();
    if (!t) return;
    if (!TIME_RE.test(t)) { setError('Time must be in 24-hour HH:MM format (e.g. 08:00).'); return; }
    if (scheduleTimes.includes(t)) { setPendingTime(''); return; }
    setScheduleTimes([...scheduleTimes, t].sort());
    setPendingTime('');
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError('Please enter the medication name.'); return; }
    if (!dosage.trim()) { setError('Please enter the dosage.'); return; }
    setSubmitting(true);
    const trimmedName = name.trim();
    const effectiveRxcui = mode === 'manual' ? null : rxcui;
    const willSkipCheck = mode === 'manual' && !effectiveRxcui;
    try {
      await createMedication({ name: trimmedName, rxcui: effectiveRxcui, dosage: dosage.trim(), frequency, schedule_times: scheduleTimes, notes: notes.trim() || null, barcode_ndc: barcodeNdc });
      if (willSkipCheck) {
        stashPostAddToast({ kind: 'unverified', newDrug: trimmedName, otherDrug: null, summary: 'We could not verify this medication, so interaction analysis was skipped.' });
      } else {
        let checkRes = null;
        try { checkRes = await checkOnAdd(trimmedName, effectiveRxcui); } catch {}
        if (checkRes && checkRes.results.length > 0) {
          const worst = [...checkRes.results].sort((a, b) => SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity])[0];
          const otherDrug = worst.drug_a.toLowerCase() === trimmedName.toLowerCase() ? worst.drug_b : worst.drug_a;
          stashPostAddToast({ kind: worst.severity, newDrug: trimmedName, otherDrug, summary: worst.summary });
          if (worst.severity === 'severe') stashPendingSevere({ newDrug: trimmedName, otherDrug, summary: worst.summary, createdAt: new Date().toISOString() });
        } else {
          stashPostAddToast({ kind: 'none', newDrug: trimmedName, otherDrug: null, summary: checkRes?.pairs_checked === 0 ? 'No other medications to compare against yet.' : 'No clinically significant interactions detected.' });
        }
      }
      navigate('/medications', { replace: true });
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="add-medication-screen">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => navigate(-1)} data-testid="add-back-btn" className="p-1 rounded-lg hover:bg-black/5"><ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} /></button>
        <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }}>Add medication</h1>
      </div>

      {/* Mode tabs */}
      <div className="flex rounded-full p-1 mb-6 gap-1" style={{ backgroundColor: '#EEF4F7' }} data-testid="add-mode-segments">
        {MODES.map((m) => {
          const active = mode === m.key;
          return (
            <button key={m.key} onClick={() => { setMode(m.key); setError(null); }} data-testid={`add-mode-tab-${m.key}`} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full text-sm font-bold transition-colors ${active ? 'text-white' : ''}`} style={active ? { backgroundColor: '#D77A52', color: '#FFFFFF' } : { color: '#D77A52' }}>
              <m.icon className="h-4 w-4" /> {m.label}
            </button>
          );
        })}
      </div>

      {error && <div data-testid="add-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}><p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p></div>}

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Name section by mode */}
        {mode === 'search' && (
          <div data-testid="add-mode-search">
            <label htmlFor="search-name" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Medication name</label>
            <input id="search-name" data-testid="add-search-input" value={query} onChange={(e) => { setQuery(e.target.value); if (e.target.value !== name) { setName(''); setRxcui(null); } }} placeholder="Start typing — e.g. 'warf'" className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
            {searching && <div className="flex items-center gap-2 mt-1"><Loader2 className="h-4 w-4 animate-spin" style={{ color: '#D77A52' }} /><span className="text-sm" style={{ color: '#4B6B79' }}>Searching RxNorm…</span></div>}
            {results.length > 0 && !name && (
              <div className="border rounded-xl overflow-hidden mt-1" style={{ borderColor: '#D7E2E8' }} data-testid="add-search-suggestions">
                {results.map((hit, idx) => (
                  <button key={`${hit.rxcui ?? 'na'}-${idx}`} type="button" onClick={() => pickSearchHit(hit)} data-testid={`add-search-suggestion-${idx}`} className="w-full flex items-center gap-2 px-4 py-2.5 border-b text-left hover:bg-gray-50" style={{ borderColor: '#D7E2E8' }}>
                    <Search className="h-4 w-4 shrink-0" style={{ color: '#4B6B79' }} />
                    <span className="text-base" style={{ color: '#0F2A35' }}>{hit.name}</span>
                  </button>
                ))}
              </div>
            )}
            {name && rxcui && (
              <div data-testid="add-search-confirmed" className="flex items-center gap-2 p-2.5 rounded-xl border mt-2" style={{ backgroundColor: '#E4F4EB', borderColor: '#1F7A4A' }}>
                <Check className="h-4 w-4" style={{ color: '#1F7A4A' }} />
                <span className="text-sm font-bold" style={{ color: '#1F7A4A' }}>Verified: {name} (RxCUI {rxcui})</span>
              </div>
            )}
          </div>
        )}
        {mode === 'manual' && (
          <div data-testid="add-mode-manual">
            <label htmlFor="manual-name" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Medication name</label>
            <input id="manual-name" data-testid="add-manual-name" value={name} onChange={(e) => { setName(e.target.value); setRxcui(null); setUnverifiedHint(true); }} placeholder="e.g. Lipitor or Atorvastatin" className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
            {unverifiedHint && name.trim() && (
              <div className="flex items-start gap-2 p-2.5 rounded-xl border mt-2" style={{ backgroundColor: '#FFF4DF', borderColor: '#B07A1F' }}>
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#B07A1F' }} />
                <span className="text-sm" style={{ color: '#B07A1F' }}>We&apos;ll save this as-is. If our database can&apos;t verify it, interaction checks may be limited.</span>
              </div>
            )}
          </div>
        )}
        {mode === 'barcode' && (
          <div data-testid="add-mode-barcode">
            <label htmlFor="ndc-input" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>NDC Code</label>
            <div className="flex gap-2">
              <input id="ndc-input" value={ndcInput} onChange={(e) => setNdcInput(e.target.value)} placeholder="e.g. 58151-156" className="flex-1 px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} data-testid="add-ndc-input" />
              <button type="button" onClick={onNdcLookup} className="px-4 py-3 rounded-xl font-bold text-white" style={{ backgroundColor: '#D77A52' }} data-testid="add-ndc-lookup-btn">Lookup</button>
            </div>
            {barcodeNdc && <p className="text-sm mt-1" style={{ color: '#4B6B79' }}>Last looked up: {barcodeNdc}</p>}
          </div>
        )}

        {/* Dosage */}
        <div>
          <label htmlFor="dosage" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Dosage</label>
          <input id="dosage" data-testid="add-dosage-input" value={dosage} onChange={(e) => setDosage(e.target.value)} placeholder="e.g. 10 mg" className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
        </div>

        {/* Frequency */}
        <div>
          <p className="text-sm font-semibold mb-2" style={{ color: '#0F2A35' }}>Frequency</p>
          <div className="flex flex-wrap gap-2">
            {FREQUENCY_OPTIONS.map((opt) => (
              <button key={opt} type="button" onClick={() => { setFrequency(opt); if (scheduleTimes.length === 0) { const d = defaultTimesForFrequency(opt); if (d.length) setScheduleTimes(d); } }} data-testid={`add-freq-${opt.replace(/\s+/g, '-')}`} className={`px-4 py-2.5 rounded-full border-[1.5px] text-sm font-bold min-h-[44px] transition-colors ${frequency === opt ? 'text-white' : ''}`} style={frequency === opt ? { backgroundColor: '#D77A52', borderColor: '#D77A52', color: '#FFFFFF' } : { borderColor: '#D77A52', color: '#D77A52' }}>
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Schedule times */}
        <div>
          <p className="text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Schedule times (optional)</p>
          <p className="text-xs mb-2" style={{ color: '#4B6B79' }}>24-hour HH:MM, e.g. 08:00.</p>
          <div className="flex flex-wrap gap-2 mb-2">
            {scheduleTimes.map((t) => (
              <button key={t} type="button" onClick={() => setScheduleTimes(scheduleTimes.filter((x) => x !== t))} data-testid={`add-time-chip-${t}`} className="flex items-center gap-1 px-4 py-2 rounded-full text-base font-bold" style={{ backgroundColor: '#EEF4F7', color: '#5B2D1F' }}>
                {t} <X className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-end">
            <input value={pendingTime} onChange={(e) => setPendingTime(e.target.value)} placeholder="HH:MM" data-testid="add-time-input" className="flex-1 px-4 py-3 rounded-xl border text-base outline-none focus:ring-2" style={{ borderColor: '#D7E2E8' }} />
            <button type="button" onClick={addTime} data-testid="add-time-add-btn" className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: '#D77A52' }}><Plus className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-semibold mb-1" style={{ color: '#0F2A35' }}>Notes (optional)</label>
          <textarea id="notes" data-testid="add-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. take with food" rows={2} className="w-full px-4 py-3 rounded-xl border text-base outline-none focus:ring-2 resize-none" style={{ borderColor: '#D7E2E8' }} />
        </div>

        <button type="submit" disabled={submitting} data-testid="add-save-btn" className="w-full py-4 rounded-xl text-lg font-extrabold text-white transition-opacity hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: '#D77A52' }}>
          {submitting ? 'Saving…' : 'Save medication'}
        </button>
      </form>
    </div>
  );
}
